import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Grid,
  Typography,
  Box,
  makeStyles,
  Button,
} from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import Alert from '@material-ui/lab/Alert';
import { statuses } from 'constants/booking';
import moment from 'logic/moment';
import CottageInfo from './CottageInfo';
import Info from './Info';

const styles = makeStyles((theme) => ({
  smGridLeft: {
    [theme.breakpoints.down('xs')]: {
      order: 1,
    },
  },
  smGridRight: {
    [theme.breakpoints.down('xs')]: {
      order: 2,
    },
  },
  mb: {
    marginBottom: '8px',
  },
}));

const BookingPreview = (props) => {
  const {
    booking, cottage, overlap, overlapAccepted, setOverlapAccepted,
    setEditMode,
  } = props;
  const classes = styles();

  if (!booking) {
    return null;
  }

  return (
    <Box p={2}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" align="center">
            {cottage.title_sv}
          </Typography>
        </Grid>
        {
          !!overlap && !!overlap.length && (
            <Grid item xs={12}>
              <Alert severity="warning" elevation={3}>
                <FormattedMessage
                  id="booking.overlap"
                  defaultMessage="Warning, there is already one or more bookings overlapping with the selected check in / check out dates"
                />
                {
                  overlap.map((ol, i) => (
                    <Typography key={i} variant="body2">
                      <Link to={`/conversations/${ol.conversation_id}/bookings/${ol.id}`} target="_blank">
                        <FormattedMessage
                          id="booking.overlap.detail"
                          defaultMessage="Booking #{id} - Check-in: {checkin} | Check-out: {checkout}"
                          values={{
                            id: ol.id,
                            checkin: moment(ol.check_in).format('LLLL'),
                            checkout: moment(ol.check_out).format('LLLL'),
                          }}
                        />
                      </Link>
                    </Typography>
                  ))
                }
              </Alert>
              {
                !overlapAccepted && (
                  <Box textAlign="center" py={2}>
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      onClick={() => setOverlapAccepted(true)}
                      className={classes.mb}
                    >
                      <FormattedMessage
                        id="booking.overlap.understand"
                        defaultMessage="I understand, continue anyway"
                      />
                    </Button>
                    &nbsp;
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={setEditMode}
                      className={classes.mb}
                    >
                      <FormattedMessage
                        id="Back"
                        defaultMessage="Back"
                      />
                    </Button>
                  </Box>
                )
              }
            </Grid>
          )
        }
        {
          (!overlap || !overlap.length || overlapAccepted) && (
            <>
              <Grid item xs={12} sm={6} className={classes.smGridRight}>
                <Info {...props} isHost preview />
              </Grid>
              <Grid item xs={12} sm={6} className={classes.smGridLeft}>
                <CottageInfo id={cottage.id} cottagePic={cottage.cottagePic} taller />
              </Grid>
            </>
          )
        }

      </Grid>
    </Box>
  );
};

BookingPreview.propTypes = {
  overlap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    conversation_id: PropTypes.number,
    check_in: PropTypes.string,
    check_out: PropTypes.string,
  })),
  booking: PropTypes.shape({
    Guest: PropTypes.shape({}).isRequired,
    Host: PropTypes.shape({}).isRequired,
    additional_information: PropTypes.string,
    booking_expiration_date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    cancellationPolicy: PropTypes.shape({}),
    check_in: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    check_out: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    cleaning_policy: PropTypes.string.isRequired,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }),
    isHost: PropTypes.bool,
    number_of_guests: PropTypes.number.isRequired,
    status: PropTypes.oneOf(Object.values(statuses)),
    total_price: PropTypes.number.isRequired,
  }),
  cottage: PropTypes.shape({
    title_sv: PropTypes.string.isRequired,
    cottagePic: PropTypes.string,
    id: PropTypes.number.isRequired,
  }).isRequired,
  cancellationPolicy: PropTypes.shape({}),
  setEditMode: PropTypes.func.isRequired,
  overlapAccepted: PropTypes.bool.isRequired,
  setOverlapAccepted: PropTypes.func.isRequired,
};

export default BookingPreview;
